import { computed, ref, type Ref, watch } from 'vue';
import { watchTriggerable } from '@vueuse/core';

/**
 * Watches a ref for re-running a backend request whenever that ref changes. The most common use case is listing data on
 * a page based on the currently selected facility ID.
 *
 * By default, `watchAndFetch` will reset its `response` ref to `defaultValue` every time the `watching` ref changes,
 * ensuring that the data on-screen always belongs with `func`'s parameters. In the case of racing requests due to quick
 * user action, the last request sent always wins.
 *
 * For user-initiated in-band reloads without a reset you can use `triggerWithoutReset`.
 *
 * `canFetch` is useful for ensuring you don't fetch data the screen doesn't yet need, i.e. you can use it to gate
 * requests behind a subsection being opened, or a setting being enabled.
 *
 * `watchAndFetch` does not return any kind of error ref on purpose. `func` is expected to always succeed, except for
 * truly exceptional situations (i.e. transient network errors, or a user reaching a page that should be inaccessible
 * to them). For these cases, our global error handler will catch the error and display a standard error modal. If an
 * error is possible within the normal operation of `func`, you should catch that error internally to `func` and return
 * a discriminated union, i.e. `{ status: 'success', thingIWant } | { status: 'error', error: string }` and have your
 * UI handle the union appropriately.
 */
export function watchAndFetch<Input, Result>(
  watching: Ref<Input>,
  defaultValue: Result,
  func: (input: Input) => Promise<Result>,
  canFetch?: Ref<boolean>,
): { loading: Ref<boolean>; response: Ref<Result>; trigger: () => unknown; triggerWithoutReset: () => unknown } {
  const response = ref(defaultValue) as Ref<Result>;
  const currentInputCounter = ref<number>(0);
  const currentInputLoaded = ref<number>(-1);
  let fetchSkipped = false;

  let doNotReset = false;
  const { trigger } = watchTriggerable(
    watching,
    async (input) => {
      if (canFetch && !canFetch.value) {
        fetchSkipped = true;
        return;
      }
      fetchSkipped = false;

      if (doNotReset) {
        doNotReset = false;
      } else {
        response.value = defaultValue;
      }
      currentInputCounter.value += 1;
      const inputID = currentInputCounter.value;

      const valueFetched = await func(input);
      if (currentInputCounter.value === inputID) {
        response.value = valueFetched;
        currentInputLoaded.value = inputID;
      }
    },
    { immediate: true },
  );

  if (canFetch) {
    watch(canFetch, (newVal) => {
      if (newVal && fetchSkipped) {
        fetchSkipped = false;
        trigger();
      }
    });
  }

  return {
    loading: computed(() => currentInputLoaded.value !== currentInputCounter.value),
    response,
    trigger,
    triggerWithoutReset: () => {
      doNotReset = true;
      return trigger();
    },
  };
}
